import api from '../../api/restaurant/restaurantDashboardApi';
import {
  RestaurantDashboard,
  RestaurantDashboardResponse,
} from '../../types/restaurant/RestaurantDashboardTypes';

const number = (value: unknown) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const withNumbers = <T extends Record<string, unknown>>(
  value: T,
  keys: string[]
) =>
  keys.reduce(
    (result, key) => ({ ...result, [key]: number(value?.[key]) }),
    value
  );

export const adaptRestaurantDashboard = (
  raw: RestaurantDashboard
): RestaurantDashboard => ({
  ...raw,
  branchId: number(raw?.branchId),
  branchName: raw?.branchName ?? '',
  businessDate: raw?.businessDate ?? '',
  generatedAt: raw?.generatedAt ?? '',
  tables: withNumbers(raw?.tables ?? ({} as RestaurantDashboard['tables']), [
    'total',
    'free',
    'occupied',
    'reserved',
    'cleaning',
    'outOfService',
    'occupancyRate',
  ]) as RestaurantDashboard['tables'],
  orders: withNumbers(raw?.orders ?? ({} as RestaurantDashboard['orders']), [
    'totalToday',
    'activeNow',
    'open',
    'confirmed',
    'inKitchen',
    'ready',
    'billing',
    'closed',
    'cancelled',
    'orderedAmount',
  ]) as RestaurantDashboard['orders'],
  sales: {
    ...(withNumbers(raw?.sales ?? ({} as RestaurantDashboard['sales']), [
      'acceptedTickets',
      'netRevenue',
      'averageTicket',
      'previousDayRevenue',
      'changePercentage',
    ]) as RestaurantDashboard['sales']),
    lastSevenDays: (raw?.sales?.lastSevenDays ?? []).map((item) => ({
      date: item.date,
      total: number(item.total),
    })),
  },
  kitchen: withNumbers(raw?.kitchen ?? ({} as RestaurantDashboard['kitchen']), [
    'pending',
    'inPreparation',
    'ready',
    'averagePreparationMinutes',
  ]) as RestaurantDashboard['kitchen'],
  reservations: withNumbers(
    raw?.reservations ?? ({} as RestaurantDashboard['reservations']),
    [
      'totalToday',
      'pending',
      'confirmed',
      'seated',
      'completed',
      'cancelled',
      'noShow',
      'upcoming',
    ]
  ) as RestaurantDashboard['reservations'],
  inventory: {
    ...(withNumbers(
      raw?.inventory ?? ({} as RestaurantDashboard['inventory']),
      ['trackedMaterials', 'lowStock', 'outOfStock', 'estimatedValue']
    ) as RestaurantDashboard['inventory']),
    alerts: (raw?.inventory?.alerts ?? []).map((alert) => ({
      ...alert,
      inventoryId: number(alert.inventoryId),
      rawMaterialId: number(alert.rawMaterialId),
      currentStock: number(alert.currentStock),
      minimumStock: number(alert.minimumStock),
    })),
  },
  topSellingItems: (raw?.topSellingItems ?? []).map((item) => ({
    ...item,
    menuItemId: number(item.menuItemId),
    quantity: number(item.quantity),
    total: number(item.total),
  })),
  recentActiveOrders: (raw?.recentActiveOrders ?? []).map((order) => ({
    ...order,
    id: number(order.id),
    total: number(order.total),
  })),
});

const RestaurantDashboardService = {
  get: async (branchId: number, date?: string) => {
    const query = date ? `?date=${encodeURIComponent(date)}` : '';
    const response = await api.fetchCustom(`/branches/${branchId}${query}`);
    const envelope = response.data as RestaurantDashboardResponse;
    return { ...envelope, data: adaptRestaurantDashboard(envelope.data) };
  },
};

export default RestaurantDashboardService;
